import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { creditsApi } from '../services/api';
import type { AdminUpdateUserCreditSettingsRequest, User } from '../types';

type FormState = {
  transcriptionCostMultiplier: string;
  bookmarkCostMultiplier: string;
  maxCreditLimit: string;
  maxCreditPurchaseAmount: string;
  maxTranscriptionDurationMinutes: string;
};

const emptyForm: FormState = {
  transcriptionCostMultiplier: '1',
  bookmarkCostMultiplier: '1',
  maxCreditLimit: '',
  maxCreditPurchaseAmount: '',
  maxTranscriptionDurationMinutes: '',
};

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : NaN;
}

function parseRequiredPositive(value: string, label: string): number {
  const trimmed = value.trim();
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(`${label} must be a positive number.`);
  }
  return n;
}

function parseOptionalPositiveInt(value: string, label: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    throw new Error(`${label} must be a positive whole number or empty for default.`);
  }
  return n;
}

function settingsToForm(settings: {
  transcriptionCostMultiplier: number;
  bookmarkCostMultiplier: number;
  maxCreditLimit: number | null;
  maxCreditPurchaseAmount: number | null;
  maxTranscriptionDurationMinutes: number | null;
}): FormState {
  return {
    transcriptionCostMultiplier: String(settings.transcriptionCostMultiplier ?? 1),
    bookmarkCostMultiplier: String(settings.bookmarkCostMultiplier ?? 1),
    maxCreditLimit:
      settings.maxCreditLimit != null ? String(settings.maxCreditLimit) : '',
    maxCreditPurchaseAmount:
      settings.maxCreditPurchaseAmount != null ? String(settings.maxCreditPurchaseAmount) : '',
    maxTranscriptionDurationMinutes:
      settings.maxTranscriptionDurationMinutes != null
        ? String(settings.maxTranscriptionDurationMinutes)
        : '',
  };
}

function extractErrorMessage(err: unknown): string {
  if (err != null && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { message?: string; error?: string } } })
      .response;
    if (response?.status === 403) {
      return (
        'Access denied (403). Your account needs the ADMIN_CREDITS permission ' +
        '(ADMIN role, or USER_MANAGER after the latest server migration). Ask an administrator to verify your roles.'
      );
    }
    const data = response?.data;
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (typeof data?.error === 'string' && data.error.trim()) {
      return data.error;
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Failed to save settings.';
}

export type UserCreditSettingsDialogProps = {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSaved?: () => void;
};

const UserCreditSettingsDialog: React.FC<UserCreditSettingsDialogProps> = ({
  open,
  user,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const userId = user != null ? Number(user.id) : NaN;

  const loadSettings = useCallback(async () => {
    if (!open || user == null || !Number.isFinite(userId)) {
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await creditsApi.getAdminCreditSettings(userId);
      if (response.data == null) {
        setError(response.message ?? 'Could not load credit settings.');
        setForm(emptyForm);
        return;
      }
      setForm(settingsToForm(response.data));
    } catch (e) {
      setError(extractErrorMessage(e));
      setForm(emptyForm);
    } finally {
      setLoading(false);
    }
  }, [open, user, userId]);

  useEffect(() => {
    if (open) {
      void loadSettings();
    } else {
      setForm(emptyForm);
      setError(null);
      setSuccess(null);
    }
  }, [open, loadSettings]);

  const handleField =
    (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const handleSave = async () => {
    if (user == null || !Number.isFinite(userId)) {
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body: AdminUpdateUserCreditSettingsRequest = {
        transcriptionCostMultiplier: parseRequiredPositive(
          form.transcriptionCostMultiplier,
          'Transcription cost multiplier',
        ),
        bookmarkCostMultiplier: parseRequiredPositive(
          form.bookmarkCostMultiplier,
          'Bookmark cost multiplier',
        ),
        maxCreditLimit: (() => {
          const n = parseOptionalNumber(form.maxCreditLimit);
          if (Number.isNaN(n)) {
            throw new Error('Max credit limit must be a number or empty.');
          }
          return n;
        })(),
        maxCreditPurchaseAmount: (() => {
          const n = parseOptionalNumber(form.maxCreditPurchaseAmount);
          if (Number.isNaN(n)) {
            throw new Error('Max credit purchase amount must be a number or empty.');
          }
          return n;
        })(),
        maxTranscriptionDurationMinutes: parseOptionalPositiveInt(
          form.maxTranscriptionDurationMinutes,
          'Max transcription duration',
        ),
      };
      await creditsApi.updateAdminCreditSettings(userId, body);
      setSuccess(
        'Settings saved. Transcription limit will apply on the user’s next transcribe (synced to mobile via Firestore when configured).',
      );
      onSaved?.();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Credit &amp; transcription limits
        {user != null ? ` — ${user.name}` : ''}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress size={32} sx={{ display: 'block', mx: 'auto', my: 3 }} />
        ) : (
          <>
            {error != null && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success != null && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            <TextField
              fullWidth
              margin="dense"
              label="Transcription cost multiplier"
              type="number"
              inputProps={{ min: 0.01, step: 0.01 }}
              value={form.transcriptionCostMultiplier}
              onChange={handleField('transcriptionCostMultiplier')}
              disabled={saving}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Bookmark cost multiplier"
              type="number"
              inputProps={{ min: 0.01, step: 0.01 }}
              value={form.bookmarkCostMultiplier}
              onChange={handleField('bookmarkCostMultiplier')}
              disabled={saving}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Max credit limit"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={form.maxCreditLimit}
              onChange={handleField('maxCreditLimit')}
              helperText="Leave empty for no per-user limit override."
              disabled={saving}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Max credit purchase amount"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={form.maxCreditPurchaseAmount}
              onChange={handleField('maxCreditPurchaseAmount')}
              helperText="Leave empty for no per-user purchase cap override."
              disabled={saving}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Max transcription duration (minutes)"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={form.maxTranscriptionDurationMinutes}
              onChange={handleField('maxTranscriptionDurationMinutes')}
              helperText="Leave empty for default (70 min). Syncs to mobile via Firestore."
              disabled={saving}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {success != null ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={loading || saving || !Number.isFinite(userId)}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserCreditSettingsDialog;
